import React, { useState, useEffect, useCallback } from 'react';
import StatsCard from './StatsCards';
import ProductTable from './ProductTable';
import Pagination from '../../shared/Pagination';
import { 
  ProductCatalogIcon,
  CollectionIcon,
  TimeIcon,
  SearchIcon,
  ArrowDown
} from '../../../public/icons';
import { useToast } from '../../../context/ToastContext';
import adminProductService from '../../../services/admin/admin-productService';

const ProductsTab = ({ onProductSelect, refreshTrigger }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    baseProducts: 0,
    collectionItems: 0,
    draftProducts: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All status');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  const { success, error } = useToast();

  const statusOptions = ['All status', 'active', 'draft', 'inactive', 'archived'];

  // ============ FIXED: Fetch products function ============
  const fetchProducts = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchQuery,
        status: statusFilter !== 'All status' ? statusFilter : undefined,
        sort: '-createdAt'
      };

      console.log('🔄 [ProductsTab] Fetching products with params:', params);

      const response = await adminProductService.getProducts(params);
      
      if (response.success) {
        console.log('✅ [ProductsTab] Products fetched:', response.data.products.length);
        setProducts(response.data.products);
        setPagination(prev => ({
          ...prev,
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.pages,
          totalItems: response.data.pagination.total
        }));
      } else {
        error(response.message || 'Failed to fetch products');
        setProducts([]);
      }
    } catch (err) {
      console.error('💥 [ProductsTab] Error fetching products:', err);
      error('Unable to load products. Please check your connection.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, pagination.itemsPerPage, error]);

  // ============ FIXED: Fetch statistics function ============
  const fetchStatistics = useCallback(async () => {
    try {
      setStatsLoading(true);
      console.log('🔄 [ProductsTab] Fetching statistics...');
      
      const response = await adminProductService.getProductStats();
      
      if (response.success) {
        console.log('✅ [ProductsTab] Statistics fetched:', response.data);
        setStats({
          baseProducts: response.data.baseProducts || 0,
          collectionItems: response.data.collectionItems || 0,
          draftProducts: response.data.draftProducts || 0
        });
      } else {
        console.error('❌ [ProductsTab] Failed to fetch statistics:', response.message);
      }
    } catch (err) {
      console.error('💥 [ProductsTab] Error fetching statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // ============ FIXED: Fetch data on mount and when refreshTrigger changes ============
  useEffect(() => {
    fetchProducts(pagination.currentPage);
    fetchStatistics();
  }, [fetchProducts, fetchStatistics, pagination.currentPage, refreshTrigger]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchProducts(1);
      } else {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, fetchProducts]);

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    fetchProducts(pageNumber);
  };

  const handleProductAction = async (productId, action) => {
    try {
      switch (action) {
        case 'view':
          const product = products.find(p => p.id === productId);
          if (product && onProductSelect) {
            onProductSelect(product);
            success(`Selected product: ${product.name}`);
          }
          break;
        
        case 'edit':
          console.log('Edit product:', productId);
          break;
        
        case 'toggle-status':
          const productToUpdate = products.find(p => p.id === productId);
          const newStatus = productToUpdate.status === 'active' ? 'inactive' : 'active';
          
          const response = await adminProductService.updateProductStatus(productId, newStatus);
          
          if (response.success) {
            success(`Product status updated to ${newStatus}`);
            fetchProducts(pagination.currentPage);
            fetchStatistics(); // Refresh stats after status change
          } else {
            error(response.message || 'Failed to update status');
          }
          break;
        
        case 'delete':
          if (window.confirm('Are you sure you want to delete this product?')) {
            const deleteResponse = await adminProductService.deleteProduct(productId);
            
            if (deleteResponse.success) {
              success('Product archived successfully');
              fetchProducts(pagination.currentPage);
              fetchStatistics(); // Refresh stats after deletion
            } else {
              error(deleteResponse.message || 'Failed to delete product');
            }
          }
          break;
        
        default:
          console.log('Action menu opened for:', productId);
      }
    } catch (err) {
      console.error('Product action error:', err);
      error('Failed to perform action. Please try again.');
    }
  };

const statsData = [
  {
    count: stats.baseProducts,
    label: 'Base Products',
    icon: <ProductCatalogIcon color='#2F8F5B'/>,
    bgColor: 'bg-[#E7F4ED]',
    isLoading: statsLoading
  },
  {
    count: stats.collectionItems,
    label: 'Collection Items',
    icon: <CollectionIcon color='#412F8F' />,
    bgColor: 'bg-[#E8E7F4]',
    isLoading: statsLoading
  },
  {
    count: stats.draftProducts,
    label: 'Draft Products',
    icon: <TimeIcon color='#8F342F' />,
    bgColor: 'bg-[#F4E7E7]',
    isLoading: statsLoading
  }
];

  if (loading && products.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
        <span className="ml-3 text-gray-600">Loading products...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="flex gap-5">
        {statsData.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Products Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-medium text-[#0E1628]">All Products</h2>
          
          <div className="flex gap-4 items-center">
            {/* Search Box */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={20} color="#6B635D" />
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 pl-10 pr-3 border w-52 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="p-2 pl-3 pr-8 border w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowDown size={16} color="#6B635D" />
              </div>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <ProductTable 
          products={products}
          onProductAction={handleProductAction}
          onProductSelect={onProductSelect}
        />

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Empty State */}
        {products.length === 0 && !loading && (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 mb-2">No products found</div>
            <div className="text-sm text-gray-500">
              {searchQuery || statusFilter !== 'All status' 
                ? 'Try adjusting your search or filter'
                : 'Add your first product to get started'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsTab;